import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { Outlet } from "react-router-dom";
import { I18nProvider, LANGS, detectLang } from "@/lib/i18n";
import { AuthProvider } from "@/context/AuthContext";
import { Layout } from "@/components/layout/Layout";
import { Toaster } from "@/components/ui/sonner";

import Home from "@/pages/Home";
import About from "@/pages/About";
import Services from "@/pages/Services";
import ServiceDetail from "@/pages/ServiceDetail";
import Products from "@/pages/Products";
import ProductDetail from "@/pages/ProductDetail";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import Technologies from "@/pages/Technologies";
import { Blog, BlogDetail } from "@/pages/Blog";
import Contact from "@/pages/Contact";
import CaseStudies from "@/pages/CaseStudies";
import Resources from "@/pages/Resources";
import Demos from "@/pages/Demos";
import ClientArea from "@/pages/ClientArea";
import { Privacy, LegalNotice } from "@/pages/Legal";
import Login from "@/pages/Login";
import Admin from "@/pages/Admin";
import ResetPassword from "@/pages/ResetPassword";
import NotFound from "@/pages/NotFound";
import Solutions from "@/pages/Solutions";
import Sectors from "@/pages/Sectors";
import HosysAI from "@/pages/HosysAI";
import Insights from "@/pages/Insights";

function RootRedirect() {
  return <Navigate to={`/${detectLang()}`} replace />;
}

function LangGate() {
  const { lang } = useParams();
  if (!LANGS.includes(lang)) return <Navigate to={`/${detectLang()}`} replace />;
  return (
    <I18nProvider lang={lang}>
      <Outlet />
    </I18nProvider>
  );
}

// Preserves query params/hash when redirecting old URLs → new IA (Sprint 1 / Fase 3)
function LangNav({ to }) {
  const { lang } = useParams();
  const location = useLocation();
  return <Navigate to={`/${lang}${to}${location.search}${location.hash}`} replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/:lang" element={<LangGate />}>
              <Route element={<Layout />}>
                <Route index element={<Home />} />

                {/* New Information Architecture (9 sections) */}
                <Route path="solutions" element={<Solutions />} />
                <Route path="sectors" element={<Sectors />} />
                <Route path="projects" element={<Projects />} />
                <Route path="projects/:slug" element={<ProjectDetail />} />
                <Route path="hosys-ai" element={<HosysAI />} />
                <Route path="insights" element={<Insights />} />
                <Route path="company" element={<About />} />
                <Route path="contact" element={<Contact />} />
                <Route path="demo" element={<Contact />} />

                {/* Legacy detail pages kept (SEO safe): */}
                <Route path="services/:slug" element={<ServiceDetail />} />
                <Route path="products/:slug" element={<ProductDetail />} />
                <Route path="blog/:slug" element={<BlogDetail />} />

                {/* 301-equivalent redirects for old top-level URLs */}
                <Route path="about" element={<LangNav to="/company" />} />
                <Route path="services" element={<LangNav to="/solutions" />} />
                <Route path="products" element={<LangNav to="/solutions" />} />
                <Route path="technologies" element={<LangNav to="/hosys-ai" />} />
                <Route path="blog" element={<LangNav to="/insights" />} />
                <Route path="resources" element={<LangNav to="/insights" />} />
                <Route path="demos" element={<LangNav to="/demo" />} />
                <Route path="case-studies" element={<LangNav to="/projects" />} />

                {/* Utility routes */}
                <Route path="client" element={<ClientArea />} />
                <Route path="privacy" element={<Privacy />} />
                <Route path="legal" element={<LegalNotice />} />

                <Route path="*" element={<NotFound />} />
              </Route>
              <Route path="login" element={<Login />} />
              <Route path="reset-password" element={<ResetPassword />} />
              <Route path="admin" element={<Admin />} />
            </Route>
            <Route path="*" element={<RootRedirect />} />
          </Routes>
        </BrowserRouter>
        <Toaster theme="dark" position="bottom-center" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
